/**
 * Samurai Mode — Scroll & Parallax Engine
 * assets/sm-scroll-effects.js
 * KUDEN by TAKAHIRO SATO | EC Renewal 2026
 *
 * Features:
 *   - [data-sm-parallax="speed"]  : background parallax
 *   - [data-sm-parallax-up="speed"]: foreground rises against scroll (product img)
 *   - .sm-reveal [data-sm-delay]  : stagger fade+slide reveal
 *   - [data-sm-marquee]           : horizontal text marquee
 */

(function () {
  if (window.__smScrollEffects) return;
  window.__smScrollEffects = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Helpers ─────────────────────────────────────────── */
  function clamp(v, min, max) {
    return Math.min(Math.max(v, min), max);
  }

  function isMobile() {
    return window.innerWidth < 769;
  }

  /* ── 1. Parallax ─────────────────────────────────────── */
  // Background: [data-sm-parallax="0.3"]
  //   The element moves at `speed` ratio relative to scroll.
  //   Positive speed = slower than scroll (classic bg parallax).
  // Foreground up: [data-sm-parallax-up="0.12"]
  //   Element drifts upward as you scroll down (product float).

  var parallaxItems = [];

  function collectParallax() {
    parallaxItems = [];
    if (REDUCED || isMobile()) return;

    document.querySelectorAll('[data-sm-parallax]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-sm-parallax')) || 0.25;
      var section = el.closest('section') || el.parentElement;
      el.style.willChange = 'transform';
      parallaxItems.push({ el: el, speed: speed, dir: 1, section: section });
    });

    document.querySelectorAll('[data-sm-parallax-up]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-sm-parallax-up')) || 0.1;
      var section = el.closest('section') || el.parentElement;
      el.style.willChange = 'transform';
      parallaxItems.push({ el: el, speed: speed, dir: -1, section: section });
    });
  }

  function tickParallax() {
    if (!parallaxItems.length) return;
    var vy = window.scrollY;
    var vh = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var sr = item.section.getBoundingClientRect();
      // Only process sections near the viewport
      if (sr.bottom < -vh || sr.top > vh * 2) return;

      var sectionCenter = sr.top + sr.height * 0.5;
      var viewCenter = vh * 0.5;
      var offset = (viewCenter - sectionCenter) * item.speed * item.dir;
      item.el.style.transform = 'translateY(' + offset.toFixed(2) + 'px)';
    });
  }

  /* ── 2. Stagger Reveal ───────────────────────────────── */
  // .sm-reveal elements fade + slide in.
  // Add [data-sm-delay="1..5"] for stagger offsets.

  function initReveal() {
    var els = document.querySelectorAll('.sm-reveal:not([data-sm-observed])');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sm-reveal--in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) {
      el.setAttribute('data-sm-observed', '1');
      io.observe(el);
    });
  }

  /* ── 3. Section Clip Reveal ──────────────────────────── */
  // .sm-section-clip — section clips in from bottom edge.

  function initSectionClip() {
    var els = document.querySelectorAll('.sm-section-clip:not([data-sm-observed])');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sm-section-clip--in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.05 });

    els.forEach(function (el) {
      el.setAttribute('data-sm-observed', '1');
      io.observe(el);
    });
  }

  /* ── 4. Marquee ──────────────────────────────────────── */
  function initMarquee() {
    document.querySelectorAll('[data-sm-marquee]:not([data-sm-marquee-init])').forEach(function (el) {
      if (REDUCED) return;
      el.setAttribute('data-sm-marquee-init', '1');
      var inner = el.querySelector('[data-sm-marquee-track]');
      if (!inner) return;
      inner.innerHTML += inner.innerHTML;
      var pxPerSec = parseFloat(el.getAttribute('data-sm-marquee')) || 40;
      var pos = 0;
      var trackW = 0;

      function measure() {
        trackW = inner.scrollWidth / 2;
      }
      measure();

      var last = null;
      function tick(ts) {
        if (!last) last = ts;
        var dt = (ts - last) / 1000;
        last = ts;
        pos -= pxPerSec * dt;
        if (Math.abs(pos) >= trackW) pos = 0;
        inner.style.transform = 'translateX(' + pos.toFixed(2) + 'px)';
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      window.addEventListener('resize', measure);
    });
  }

  /* ── RAF scroll loop ─────────────────────────────────── */
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      tickParallax();
      ticking = false;
    });
  }

  /* ── Init ────────────────────────────────────────────── */
  function init() {
    collectParallax();
    initReveal();
    initSectionClip();
    initMarquee();
    tickParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      collectParallax();
      tickParallax();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-init on section reload
  document.addEventListener('shopify:section:load', function () {
    collectParallax();
    initReveal();
    initSectionClip();
    initMarquee();
    tickParallax();
  });

})();
